import { handleAppLink } from "@actions/deepLink.actions";
import QtySelectBottomView from "@components/ProductDisplayUnit/QtySelectBottomView";
import {
  HOMEPAGE_LOADED,
  ORDER_FLOW_EVENTS,
} from "@constants/analyticsEventsCT";
import colors from "@constants/color";
import useInAppRating from "@hooks/useInAppRating";
import { getItem, setItem, removeItem } from "@lib/storage";
import { useFocusEffect } from "@react-navigation/native";
import { STATE_STATUS } from "@reducers/actionTypes";
import { actions as configActions } from "@reducers/configReducer";
import { actions as customerActions } from "@reducers/customerReducer";
import { actions } from "@reducers/delayDataReducer";
import { actions as nudgeActions } from "@reducers/nudgeReducer";
import { actions as refreshActions } from "@reducers/refreshReducer";
import { actions as homeWidgetActions } from "@reducers/homeWidgetReducer";
import analytics from "@services/analytics";
import { getHomePageNew } from "@services/cmsV2-api";
import {
  inAppUpdateListener,
  invokeInAppUpdate,
} from "@utils/invokeInAppUpdate";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import { connect, useDispatch } from "react-redux";
import Cross from "../../assets/svg/Cross";
import { VideoBanner } from "../../components/CMS/Banner";
import FetchCMSEntity from "../../components/CMS/RenderCMSEntity";
import useCMSEvents from "../../hooks/useCMSEvents";
import toast from "../../utils/toast";
import useGetVideoTime, {
  GetVideoTimeContextProvider,
} from "../../utils/useGetVideoTime";
import CartBottomStrip from "./CartBottomStrip";
import ClPaymentAndAddress from "./ClPaymentAndAddress";
import Header from "./Header";
import SuspensionMsg from "./SuspensionMsg";
import HomePageWidget from "./HomePageWidget/HomePageWidget";

function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

const FETCH_LIMIT = 10;

const HomeHeader = memo(() => {
  const [showIj] = useState(true);
  const [applinkHandled, setApplinkHandled] = useState(false);
  const dispatch = useDispatch();

  useFocusEffect(
    useCallback(() => {
      checkForNewUser();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showIj]),
  );

  const checkForNewUser = async () => {
    try {
      if (!applinkHandled) {
        dispatch(handleAppLink());
        setApplinkHandled(true);
      }
    } catch (e) {}
  };

  return <Header />;
});

const OnboardingVideo = ({ videoLink = "" }) => {
  const [state] = useGetVideoTime();

  useEffect(() => {
    if (state.isCompleted) {
      stopVideoSession();
    }
  }, [state.isCompleted]);

  const stopVideoSession = async () => {
    await setItem("times_onboarding_video_shown", String(10000));
  };

  return (
    <View style={{ paddingVertical: 10 }}>
      <VideoBanner
        title_ref="Homepage Video"
        video_link={videoLink}
        fromHome={true}
      />
    </View>
  );
};

const StickySample = (props) => {
  const { homePage } = props;
  const [dataProvider, setDataProvider] = useState(
    homePage?.data?.list_items || [],
  );
  const [isLoading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [endReached, setEndReached] = useState(false);
  const [showOtherSections, setOtherSections] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [, setShowRatingModal] = useState(false);
  const [, setRatingModal] = useState({
    checkCLRating: "",
    order_id: [],
    images: [],
  });
  const trackEvent = useCMSEvents("Home");
  const [homePageLoaded] = useState(false);
  const [, setConfigState] = useState(props.configStatus.status);
  const [delayedStatus, setDelayedStatus] = useState(props.delayedDataReducer);
  const nextOffset = useRef(homePage.nextOffset || 0);
  const prevDelayedStatus = usePrevious(delayedStatus);

  const ShowInAppRating = useInAppRating();

  useEffect(() => {
    invokeInAppUpdate();
    const callBack = (status, bytesDownloaded, totalBytesToDownload) => {
      toast.showShortToast("Downloading update...");
    };
    const unsubscribe = inAppUpdateListener(callBack);
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setConfigState(props.configStatus.status);
    setDelayedStatus(props.delayedDataReducer);
    setEndReached(props.endReached);
  }, [props]);

  useEffect(() => {
    props.fetchDelayedData();
    setDelayedStatus(props.delayedDataReducer);
    handleVideoVisility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch fresh data when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      props.fetchHomePageWidget();
    }, [props.fetchHomePageWidget]),
  );

  useEffect(() => {
    if (
      prevDelayedStatus &&
      prevDelayedStatus.status === STATE_STATUS.FETCHING &&
      delayedStatus.status === STATE_STATUS.FETCHED
    ) {
      if (!__DEV__) {
        analytics.trackEvent(HOMEPAGE_LOADED);
      }
      console.log(props);
      const { checkOrderRating, inAppRating, nudge } = delayedStatus;
      if (nudge) {
        setShowNudge(true);
      }
      if (checkOrderRating) {
        setRatingModal(checkOrderRating);
        setShowRatingModal(true);
        analytics.trackEvent(ORDER_FLOW_EVENTS.DELIVERY_RATING_STARTED, {});
      }
      if (inAppRating && inAppRating.show_app_rating_modal) {
        ShowInAppRating(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedStatus]);

  const handleVideoVisility = async () => {
    const { appSettings } = props;
    const { NUM_TIMES_SHOW_OB_VIDEO = 0 } = appSettings || {};
    const isSessionExists = await getItem("startSession");
    const timesVideoShownAfterSignup = await getItem(
      "times_onboarding_video_shown",
    );
    if (isSessionExists) {
      if (typeof NUM_TIMES_SHOW_OB_VIDEO === "number") {
        if (timesVideoShownAfterSignup) {
          if (Number(timesVideoShownAfterSignup) < NUM_TIMES_SHOW_OB_VIDEO) {
            await setItem(
              "times_onboarding_video_shown",
              String(Number(timesVideoShownAfterSignup) + 1),
            );
          } else {
            // Remove the main signup session
            await removeItem("signupSessionVideo");
          }
        } else {
          await setItem("times_onboarding_video_shown", String(1));
          if (Number(timesVideoShownAfterSignup) <= NUM_TIMES_SHOW_OB_VIDEO) {
          }
        }
      }
      await removeItem("startSession");
    }
  };

  const fetchHomePage = async ({ offset = 0, limit = FETCH_LIMIT }) =>
    getHomePageNew({ offset, limit }).then((res) => res.data);

  const handleFlatListScroll = (event) => {
    if (event.nativeEvent.contentOffset.y > 277) {
      setOtherSections(true);
    }
    if (event.nativeEvent.contentOffset.y < 277) {
      setOtherSections(false);
    }
  };

  const ListFooterComponent = () => {
    return (
      <View style={isLoading ? styles.listFooterComponent : null}>
        <ActivityIndicator color="red" animating={isLoading} />
        <View style={styles.height_18} />
      </View>
    );
  };

  const onEnd = async () => {
    if (isLoading || endReached) {
      return;
    }
    setLoading(true);
    const limit = FETCH_LIMIT;
    try {
      const data = await fetchHomePage({ limit, offset: nextOffset.current });
      if (data.canFetchMore || nextOffset.current !== -1) {
        const listItems = data?.data?.list_items || [];
        setDataProvider([
          ...dataProvider,
          ...listItems.map((item) => item ?? { type: "" }),
        ]);
        nextOffset.current = data.nextOffset;
      } else {
        setEndReached(true);
      }
      setLoading(false);
    } catch (e) {
      console.log("errror is ", e);
      setLoading(false);
    }
  };

  const _renderItem = ({ item }) => {
    const { cms_item_id, item_limit, type = "" } = item || {};
    const { appSettings } = props;
    const { OB_VIDEO_URL } = appSettings || {};

    if (!type) {
      return null;
    }

    switch (type) {
      case "NEW_CL_VIDEO":
        return (
          <View key={cms_item_id}>
            <GetVideoTimeContextProvider>
              <OnboardingVideo videoLink={OB_VIDEO_URL} />
            </GetVideoTimeContextProvider>
          </View>
        );
      default:
        return (
          <View key={cms_item_id}>
            <FetchCMSEntity
              trackEvent={trackEvent}
              type={type}
              id={cms_item_id}
              item_limit={item_limit}
              {...item}
            />
          </View>
        );
    }
  };

  const {
    navigation,
    selectedContact,
    setSelectedContact,
    nudge,
    cartDetails,
    cartItems,
    user,
  } = props;

  const _keyExtractor = (item, index) => {
    return `${item?.cms_item_id ?? index}`;
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Re-fetch home page widget data
      props.fetchHomePageWidget();
      
      // Re-fetch home page CMS data
      // const data = await fetchHomePage({ offset: 0, limit: FETCH_LIMIT });
      // setDataProvider(data?.data?.list_items || []);
      // nextOffset.current = data.nextOffset;
      // setEndReached(!data.canFetchMore && data.nextOffset === -1);
    } catch (error) {
      console.log('Error refreshing home page:', error);
    } finally {
      setRefreshing(false);
    }
  }, [props.fetchHomePageWidget]);

  return (
    <>
      <HomeHeader
        {...{
          navigation,
          showOtherSections,
          homePageLoaded,
          trackEvent,
        }}
      />
      <ClPaymentAndAddress user={user} />
      <SuspensionMsg user={user} />
      <FlatList
        ListHeaderComponent={HomePageWidget}
        renderAheadOffset={10}
        data={dataProvider}
        renderItem={_renderItem}
        onScroll={handleFlatListScroll}
        showsVerticalScrollIndicator={false}
        onEndReached={onEnd}
        ListFooterComponent={ListFooterComponent}
        keyExtractor={_keyExtractor}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
      />
      <QtySelectBottomView />
      {selectedContact ? (
        <CartBottomStrip
          orderClick={() => {
            props.resetHeaderTitle();
            navigation.push("ConfirmOrder");
            analytics.trackEvent(ORDER_FLOW_EVENTS.CHECKOUT_STARTED, {
              screen: "HOME",
              cart_value: cartDetails?.total || 0,
              cart_quantity: Array.isArray(cartItems)
                ? cartItems.reduce((acc, curr) => (acc += curr.quantity), 0)
                : "",
            });
          }}
          onCrossClick={() => {
            setSelectedContact(false);
            analytics.trackEvent(ORDER_FLOW_EVENTS.CX_CANCELLED);
          }}
          total={cartDetails?.total || 0}
          item={cartItems.length}
          name={selectedContact.user_name}
        />
      ) : (
        nudge.nudge &&
        showNudge && (
          <>
            <View
              style={{
                backgroundColor: "#333333",
                position: "absolute",
                paddingHorizontal: 15,
                paddingVertical: 10,
                width: "100%",
                justifyContent: "space-between",
                bottom: 0,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  color: "white",
                  fontFamily: "NotoSansDevanagari-Regular",
                }}
              >
                {nudge.nudge.text}
              </Text>
              <Pressable
                testID="rvc-nudge-navigate"
                onPress={() => {
                  if (nudge.nudge.navigateTo === "Location") {
                    // Unused variable #234233
                    // getLocation();
                  } else {
                    if (nudge.nudge.navigateTo === "TLAddress") {
                      navigation.navigate(nudge.nudge.navigateTo, {
                        fromCiAddressModal: true,
                        pincode: user.pincode,
                        city: user.city,
                      });
                    } else navigation.navigate(nudge.nudge.navigateTo);
                  }
                }}
                style={{
                  backgroundColor: colors.primary,
                  paddingVertical: 4,
                  paddingHorizontal: 10,
                  borderRadius: 6,
                }}
              >
                <Text
                  style={{
                    color: "white",
                    fontFamily: "NotoSansDevanagari-Regular",
                  }}
                >
                  {nudge.nudge.ctaText}
                </Text>
              </Pressable>
            </View>
            <TouchableOpacity
              testID="rvc-nudge-close"
              onPress={() => {
                setShowNudge(false);
              }}
              style={{
                borderRadius: 100,
                padding: 8,
                backgroundColor: "#333",
                position: "absolute",
                bottom: 165,
                left: 0,
                marginLeft: 10,
                zIndex: 999,
              }}
            >
              <Cross fill="white" />
            </TouchableOpacity>
          </>
        )
      )}
    </>
  );
};

const mstp = ({
  configReducer: {
    fullCategoryList = [],
    homePage = {},
    status,
    appSettings,
    leaderCardCount,
    leaderCardOpen,
  },
  delayedDataReducer = {},
  customer: { selectedContact },
  cart: { cartDetails, cartItems },
  nudge,
  auth: { user },
  refresh,
}) => {
  return {
    categories: fullCategoryList,
    delayedDataReducer,
    selectedContact,
    cartDetails,
    cartItems,
    nudge,
    homePage,
    configStatus: status,
    appSettings,
    user,
    refresh,
    leaderCardCount,
    leaderCardOpen,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchDelayedData: () => dispatch(actions.fetchDelayedData()),
    setNudge: (data) => dispatch(nudgeActions.setNudge(data)),
    setSelectedContact: (data) =>
      dispatch(customerActions.setSelectedContact(data)),
    resetHeaderTitle: () => dispatch(configActions.setHeaderTitle("")),
    setRefreshTimeStamp: () =>
      dispatch(refreshActions.setRefreshTimeStamp("Home")),
    resetRefreshTimeStamp: () =>
      dispatch(refreshActions.resetRefreshTimeStamp("Home")),
    handleLeaderCardOpen: (bool) =>
      dispatch(configActions.handleLeaderCardOpen(bool)),
    fetchHomePageWidget: () => dispatch(homeWidgetActions.fetchHomePageWidget()),
  };
};

export default connect(mstp, mapDispatchToProps)(StickySample);

const styles = StyleSheet.create({
  listFooterComponent: {
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  height_18: {
    height: 18,
  },
});
